import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchMarketHealth } from '../FunViz/data/dataService';
import { selectLag } from './strategyLagSelect';

import DimensionBars from './DimensionBars';
import ScoreChart from './ScoreChart';
import CollapsibleCard from '../FunViz/components/CollapsibleCard';
import StrategyChart from './StrategyChart';
import { STRATEGY_MODES } from './ModeSelector';
import ModelState from './ModelState';
import TriggerPipeline from './TriggerPipeline';
import HeartbeatStrip from './HeartbeatStrip';
import DrawdownChart from './DrawdownChart';
import TradeHistory from './TradeHistory';
import Spinner from '../common/Spinner';
import { STRATEGY_CONFIGS, getStrategyScore } from './strategyConfigs';
import styles from './MarketHealth.module.css';

const MODE_STORAGE_KEY = 'marketHealthMode';
const DEFAULT_MODE = 'adaptiveBalanced';
const VALID_MODES = STRATEGY_MODES.map(({ key }) => key);

// Every strategy defaults to the realistic 1-day execution lag. Market health
// updates hourly on weekdays during US market hours (14:30–21:30 UTC), with the
// after-close 21:30 run as the day's final; a user acting on the close can only
// trade the next morning — lag ON is the honest default across the board. Both
// permutations are still precomputed in the payload, so the toggle can flip
// either way.
const DEFAULT_LAG = true;

const readStoredMode = () => {
  try {
    const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
    return VALID_MODES.includes(stored) ? stored : DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE;
  }
};

const persistMode = (mode) => {
  try { window.localStorage.setItem(MODE_STORAGE_KEY, mode); } catch {}
};

const formatAsOf = (asOf) => {
  if (!asOf) return null;
  const parsed = new Date(asOf);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

/**
 * The Market Health tab (spec §9): fetches `GET /main/market-health`
 * via `fetchMarketHealth` (see dataService.js) once on mount and renders the
 * Risk Pressure bar, dimension bars, score-history chart, drawdown chart,
 * strategy chart and trade history.
 *
 * Mirrors useScreener/Screener's status-driven rendering convention:
 * `'loading'` while the request is in flight, `'unavailable'` for the
 * backend's HTTP 503 "not computed yet" case (spec §7.2/§10 — this is the
 * expected state right after a fresh deploy, not an error), and `'error'`
 * for anything else that failed. Only `'ok'` renders the real page content.
 */
const MarketHealth = () => {
  const [status, setStatus] = useState('loading');
  const [payload, setPayload] = useState(null);
  const [mode, setMode] = useState(readStoredMode);
  // 1-day execution lag toggle. Both permutations arrive in the payload
  // (strategy[mode].lagOn/lagOff, per-day history fields, signals[mode]), so
  // flipping this switch updates every strategy display live with no refetch.
  // Defaults ON; switching strategy preserves the user's current choice.
  const [lag, setLag] = useState(DEFAULT_LAG);

  useEffect(() => { persistMode(mode); }, [mode]);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');

    fetchMarketHealth().then((response) => {
      if (cancelled) return;
      if (response.status === 'ok' && response.current) {
        setPayload(response);
        setStatus('ok');
      } else if (response.status === 'unavailable') {
        setStatus('unavailable');
      } else {
        setStatus('error');
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  if (status === 'loading') {
    return (
      <div className={styles.page}>
        <section className={styles.card}>
          <h2 className={styles.title}>Market Health</h2>
          <Spinner size="lg" block label="Computing market health…" />
        </section>
      </div>
    );
  }

  if (status === 'unavailable') {
    return (
      <div className={styles.page}>
        <section className={styles.card}>
          <h2 className={styles.title}>Market Health</h2>
          <p className={styles.statusMessage} role="status">
            Market Health hasn't computed its first score yet. It updates hourly
            during US market hours — check back soon.
          </p>
        </section>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className={styles.page}>
        <section className={styles.card}>
          <h2 className={styles.title}>Market Health</h2>
          <p className={styles.statusMessage} role="alert">
            Couldn't load Market Health — please try again.
          </p>
        </section>
      </div>
    );
  }

  const { asOf, current, history = [], strategy } = payload;
  const { dimensions, signals, triggerInfo,
    adaptiveBalancedFeatures,
    balancedFeatures,
    adaptiveFeatures,
    conservativeConfirmFlags } = current;

  // The canonical, always-computed risk score for the selected strategy,
  // resolved with the 3-step priority (persisted field → client-side
  // susceptibility → null). Shown UNCHANGED in the Risk Pressure bar, ModelState
  // and charts. Higher = more risk; crossing the boundary is context, never a trade.
  const modeScore = getStrategyScore(current, mode);
  const config = STRATEGY_CONFIGS[mode] ?? STRATEGY_CONFIGS.adaptiveBalanced;

  const asOfLabel = formatAsOf(asOf);

  const modeTrigger = (() => {
    const info = triggerInfo?.[mode];
    if (!info) return null;
    return { ...info, ...(lag ? info.lagOn : info.lagOff) };
  })();

  return (
    <div className={styles.page}>
      <HeartbeatStrip
        history={history}
        signals={signals}
        mode={mode}
        onSelectMode={setMode}
      />

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <h2 className={styles.title}>Market Health<span className={styles.betaTag}>Beta</span></h2>
          <div className={styles.cardHeaderControls}>
            {asOfLabel && <span className={styles.asOf}>Last updated {asOfLabel}</span>}
          </div>
        </div>
        <ModelState
          signal={selectLag(signals?.[mode], lag)}
          score={modeScore}
          mode={mode}
          riskBoundaryLabel={config.riskBoundaryLabel}
          triggerInfo={modeTrigger}
        >
          <TriggerPipeline
            triggerInfo={modeTrigger}
            mode={mode}
            score={modeScore}
            position={selectLag(signals?.[mode], lag)?.position}
            config={config}
          />
        </ModelState>
        <div className={styles.summaryCard}>
          <div className={styles.barsColumn}>
            <h3 className={styles.barsHeading}>What's driving today's reading</h3>
            <DimensionBars
              dimensions={dimensions}
              mode={mode}
              score={modeScore}
              adaptiveFeatures={mode === 'adaptiveBalanced' ? adaptiveBalancedFeatures : mode === 'balanced' ? balancedFeatures : adaptiveFeatures}
              conservativeConfirmFlags={conservativeConfirmFlags}
              triggerActive={modeTrigger?.active ?? false}
            />
            <p className={styles.barsFootnote}>
              The score and factors are a visual summary of market conditions — the actual signal is the Invested/Cash state above.
            </p>
          </div>
        </div>
      </section>

      <section className={styles.card}>
        <h2 className={styles.title}>Score History</h2>
        <ScoreChart
          history={history}
          score={modeScore}
          mode={mode}
          lag={lag}
        />
      </section>

      {/* Secondary cards are collapsible (collapsed by default), reusing the
          Analysis page's CollapsibleCard so open/closed state persists per
          card in localStorage. The summary + Score History cards above stay
          always-open. */}
      <CollapsibleCard title="Drawdown from Peak" storageKey="marketHealthCard.drawdown">
        <DrawdownChart history={history} strategy={strategy} mode={mode} lag={lag} />
      </CollapsibleCard>

      <CollapsibleCard title="S&P 500 — Market Health Strategy" storageKey="marketHealthCard.strategy">
        <StrategyChart history={history} strategy={strategy} mode={mode} lag={lag} />
      </CollapsibleCard>

      <CollapsibleCard title="Strategy Performance" storageKey="marketHealthCard.performance">
        <TradeHistory history={history} strategy={strategy} mode={mode} lag={lag} onLagChange={setLag} />
      </CollapsibleCard>

      <p className={styles.disclaimer}>
        Past performance, including walk-forward validated results, does not predict future
        results. Backtested results are hypothetical — they reflect specific crisis episodes
        whose recurrence, timing, and shape are unknown. This is a rules-based research model
        for educational purposes, not investment advice. The model's thresholds and weights
        are published for transparency and reproducibility.
      </p>

      <p className={styles.researchLink}>
        Read about the methodology and validation behind this score on our{' '}
        <Link to="/blog">blog</Link>.
      </p>
    </div>
  );
};

export default MarketHealth;
